import { yup } from 'common/validation/initYup';
import { RichiestaE002 } from 'server/schema/integrations/pdnd/schema/schemas';

export enum PDNDVerificationType {
  FiscalCode = 'fiscalCode',
  Registry = 'registry',
  Id = 'id'
}
export enum PDNDVerificationLocationType {
  Italian = 'italian',
  Foreign = 'foreign',
  Exceptional = 'exceptional'
}

type BornType = Exclude<
  RichiestaE002['criteriRicerca']['datiNascita'],
  undefined
>['luogoNascita'];

export type IPDNDVerificationRequestRegistrySearch = Omit<
  RichiestaE002['criteriRicerca'] & {
    datiNascita: RichiestaE002['criteriRicerca']['datiNascita'] & {
      luogoNascita?: BornType & {
        locationType?: PDNDVerificationLocationType;
      };
    };
  },
  'idANPR' | 'codiceFiscale'
>;

export interface IPDNDVerificationRequest {
  search: {
    type: PDNDVerificationType;
    idANPR?: RichiestaE002['criteriRicerca']['idANPR'];
    codiceFiscale?: RichiestaE002['criteriRicerca']['codiceFiscale'];
    registry?: IPDNDVerificationRequestRegistrySearch;
  };
  referenceDate: Date;
  reason: string;
}

export const pdndVerificationRequestValidator: yup.ObjectSchema<IPDNDVerificationRequest> = yup
  .object({
    search: yup
      .object({
        type: yup
          .string()
          .oneOfEnum(PDNDVerificationType)
          .required()
          .default(PDNDVerificationType.FiscalCode) as yup.StringSchema<
          PDNDVerificationType
        >,

        /** Identificativo Unico Nazionale */
        idANPR: yup
          .string()
          .notRequired()
          .label('ID ANPR')
          .when(
            'type',
            (type: PDNDVerificationType, schema: yup.StringSchema) =>
              type === PDNDVerificationType.Id
                ? schema.required()
                : schema.strip(true)
          ),

        /** Ricerca per CF */
        codiceFiscale: yup
          .string()
          .notRequired()
          .trim()
          .label('Codice Fiscale')
          .when(
            'type',
            (type: PDNDVerificationType, schema: yup.StringSchema) =>
              type === PDNDVerificationType.FiscalCode
                ? schema.required()
                : schema.strip(true)
          ),

        /** Ricerca per dati anagrafici */
        registry: yup
          .object({
            cognome: yup.string().notRequired().trim().label('Cognome'),
            nome: yup.string().notRequired().trim().label('Nome'),
            senzaNome: yup
              .string()
              .notRequired()
              .when(
                '$isSubmit',
                (isSubmit: boolean, schema: yup.StringSchema) =>
                  isSubmit
                    ? schema.transform(value =>
                        value === 'true' ? '1' : undefined
                      )
                    : schema
              )
              .label('Senza Nome'),
            senzaCognome: yup
              .string()
              .notRequired()
              .when(
                '$isSubmit',
                (isSubmit: boolean, schema: yup.StringSchema) =>
                  isSubmit
                    ? schema.transform(value =>
                        value === 'true' ? '1' : undefined
                      )
                    : schema
              )
              .label('Senza Cognome'),
            sesso: yup.string().notRequired().label('Sesso'),
            datiNascita: yup
              .object({
                dataEvento: yup.date().italianFormat().notRequired(),
                senzaGiorno: yup
                  .string()
                  .notRequired()
                  .when(
                    '$isSubmit',
                    (isSubmit: boolean, schema: yup.StringSchema) =>
                      isSubmit
                        ? schema.transform(value =>
                            value === 'true' ? '1' : undefined
                          )
                        : schema
                  )
                  .label('Senza Giorno'),
                senzaGiornoMese: yup
                  .string()
                  .notRequired()
                  .when(
                    '$isSubmit',
                    (isSubmit: boolean, schema: yup.StringSchema) =>
                      isSubmit
                        ? schema.transform(value =>
                            value === 'true' ? '1' : undefined
                          )
                        : schema
                  )
                  .label('Senza Giorno e Mese'),
                luogoNascita: yup
                  .object({
                    locationType: yup
                      .string()
                      .oneOfEnum(PDNDVerificationLocationType)
                      .required()
                      .default(
                        PDNDVerificationLocationType.Italian
                      ) as yup.StringSchema<PDNDVerificationLocationType>,
                    comune: yup
                      .object({
                        nomeComune: yup
                          .string()
                          .notRequired()
                          .trim()
                          .label('Nome Comune'),
                        siglaProvinciaIstat: yup
                          .string()
                          .notRequired()
                          .trim()
                          .label('Sigla Provincia')
                      })
                      .notRequired()
                      .when(
                        'locationType',
                        (
                          locationType: PDNDVerificationLocationType,
                          schema: yup.StringSchema
                        ) =>
                          locationType === PDNDVerificationLocationType.Italian
                            ? schema.required()
                            : schema.strip(true)
                      )
                      .label('Comune'),
                    localita: yup
                      .object({
                        descrizioneStato: yup
                          .string()
                          .notRequired()
                          .trim()
                          .label('Stato di Nascita'),
                        codiceStato: yup
                          .string()
                          .notRequired()
                          .trim()
                          .label('Codice Stato')
                      })
                      .when(
                        'locationType',
                        (
                          locationType: PDNDVerificationLocationType,
                          schema: yup.StringSchema
                        ) =>
                          locationType === PDNDVerificationLocationType.Foreign
                            ? schema.required()
                            : schema.strip(true)
                      )
                      .notRequired()
                      .label('Località')

                    // Non richiesto per la ricerca.
                    // luogoEccezionale: yup
                    //   .string()
                    //   .notRequired()
                    //   .default('S')
                    //   .label('Luogo Eccezionale')
                  })
                  .notRequired()
              })
              .notRequired()
          })
          .when(
            'type',
            (
              type: PDNDVerificationType,
              schema: yup.ObjectSchema<IPDNDVerificationRequestRegistrySearch>
            ) =>
              type === PDNDVerificationType.Registry
                ? schema.required()
                : schema.strip(true)
          ) as yup.ObjectSchema<IPDNDVerificationRequestRegistrySearch>
      })
      .required(),
    referenceDate: yup
      .date()
      .required()
      .default(() => new Date()),
    reason: yup.string().required().trim().label("Motivo dell'accertamento")
  })
  .required();

// Elenco log
export const pdndLogsQueryValidator = yup
  .object({
    page: yup.number().required().integer().min(1),
    pageSize: yup.number().required().integer().min(5).max(50).default(20),
    userFullName: yup.string().notRequired(),
    operationId: yup.string().notRequired()
  })
  .required();

export type IPDNDLogsQueryDto = yup.InferType<typeof pdndLogsQueryValidator>;

export interface IPDNDVerificationLog {
  id: number;
  operationId: string;
  userId: number;
  message: string;
  userFullName: string;
  createdAt: Date;
  request: IPDNDVerificationRequest;
}
